import { Component, Injector, NgZone, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { AppComponentBase } from '@shared/common/app-component-base';
import { MazarsNotificaionServiceProxy, NotificationServiceProxy, UserNotification, UserNotificationState } from '@shared/service-proxies/service-proxies';
import { DateTime } from 'luxon';
import { LazyLoadEvent } from 'primeng/api';
import { Paginator } from 'primeng/paginator';
import { Table } from 'primeng/table';
import { DialogInfo, IFormattedUserNotification, UserNotificationHelper } from './UserNotificationHelper';
import { finalize, take } from 'rxjs/operators';
import { DateTimeService } from '@app/shared/common/timing/date-time.service';
import { FormBuilder, FormGroup } from '@angular/forms';

@Component({
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.less'],
    encapsulation: ViewEncapsulation.None,
    animations: [appModuleAnimation()],
})
export class NotificationsComponent extends AppComponentBase implements OnInit {
    @ViewChild('dataTable', { static: true }) dataTable: Table;
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    readStateFilter = 'ALL';
    loading = false;
    form: FormGroup;

    constructor(
        injector: Injector,
        private _mazarsNotificaionServiceProxy: MazarsNotificaionServiceProxy,
        private _notificationService: NotificationServiceProxy,
        private _userNotificationHelper: UserNotificationHelper,
        private _dateTimeService: DateTimeService,
        private fb: FormBuilder,
        public _zone: NgZone
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.form = this.fb.group({ implementingDate: [[this._dateTimeService.getStartOfDay(), this._dateTimeService.getEndOfDay()]] });
        this.registerToEvents();
    }

    registerToEvents() {
        let self = this;

        this.subscribeToEvent('abp.notifications.received', (userNotification) => {
            self._zone.run(() => {
                this.getNotifications();
            });
        });

        this.subscribeToEvent('app.notifications.notificationMessage', (result) => {
            self._zone.run(() => {
                if (result?.content) {
                    this.getNotifications();
                }
            });
        });

        this.subscribeToEvent('app.notifications.refreshNotifications', (result) => {
            self._zone.run(() => {
                if (result === true) {
                    this.getNotifications();
                }
            });
        });
    }

    reloadPage(): void {
        this.paginator.changePage(this.paginator.getPage());
    }

    setAsRead(record: any): void {
        this.setNotificationAsRead(record, () => {
            this.reloadPage();
        });
    }

    isRead(record: any): boolean {
        return record.formattedNotification.state === 'READ';
    }

    fromNow(date: DateTime): string {
        return this._dateTimeService.fromNow(date);
    }

    fromNowLocal(date: DateTime): string {
        return this._dateTimeService.fromNowLocal(date);
    }

    fromNowUtc(date: DateTime): string {
        return this._dateTimeService.fromNowUtc(date);
    }

    formatRecord(record: any): IFormattedUserNotification {
        return this._userNotificationHelper.format(record, false);
    }

    formatNotification(record: any): string {
        const formattedRecord = this.formatRecord(record);
        return abp.utils.truncateStringWithPostfix(formattedRecord.text, 120);
    }

    formatNotifications(records: any[]): any[] {
        const formattedRecords = [];
        for (const record of records) {
            record.formattedNotification = this.formatRecord(record);
            formattedRecords.push(record);
        }
        return formattedRecords;
    }

    openUrl(url: string) {
        this._userNotificationHelper.openUrl(url);
    }

    truncateString(text: any, length: number): string {
        return abp.utils.truncateStringWithPostfix(text, length);
    }

    getNotifications(event?: LazyLoadEvent): void {
        if (this.primengTableHelper.shouldResetPaging(event)) {
            this.paginator.changePage(0);

            if (this.primengTableHelper.records && this.primengTableHelper.records.length > 0) {
                return;
            }
        }

        this.primengTableHelper.showLoadingIndicator();

        this._notificationService
            .getUserNotifications(
                this.readStateFilter === 'ALL' ? undefined : UserNotificationState.Unread,
                this.form.controls.implementingDate.value[0].toUTC(),
                this.form.controls.implementingDate.value[1].toUTC(),
                this.primengTableHelper.getMaxResultCount(this.paginator, event),
                this.primengTableHelper.getSkipCount(this.paginator, event)
            )
            .pipe(finalize(() => this.primengTableHelper.hideLoadingIndicator()))
            .subscribe((result) => {
                this.primengTableHelper.totalRecordsCount = result.totalCount;
                this.primengTableHelper.records = this.formatNotifications(result.items);
                this.primengTableHelper.hideLoadingIndicator();
            });
    }

    setAllNotificationsAsRead(): void {
        this._userNotificationHelper.setAllAsRead(() => {
            this.getNotifications();
        });
    }

    openNotificationSettingsModal(): void {
        this._userNotificationHelper.openSettingsModal();
    }

    setNotificationAsRead(userNotification: UserNotification, callback: () => void): void {
        this._userNotificationHelper.setAsRead(userNotification.id, () => {
            if (callback) {
                callback();
            }
        });
    }

    deleteNotification(userNotification: UserNotification): void {
        this.message.confirm(this.l('NotificationDeleteWarningMessage'), this.l('AreYouSure'), (isConfirmed) => {
            if (isConfirmed) {
                this._notificationService.deleteNotification(userNotification.id).subscribe(() => {
                    this.reloadPage();
                    this.notify.success(this.l('SuccessfullyDeleted'));
                });
            }
        });
    }

    deleteNotifications() {
        this.message.confirm(this.l('DeleteListedNotificationsWarningMessage'), this.l('AreYouSure'), (isConfirmed) => {
            if (isConfirmed) {
                this._notificationService
                    .deleteAllUserNotifications(
                        this.readStateFilter === 'ALL' ? undefined : UserNotificationState.Unread,
                        this._dateTimeService.getStartOfDayForDate(this.form.controls.implementingDate.value[0]),
                        this._dateTimeService.getEndOfDayForDate(this.form.controls.implementingDate.value[1]).endOf('day')
                    )
                    .subscribe(() => {
                        this.reloadPage();
                        this.notify.success(this.l('SuccessfullyDeleted'));
                    });
            }
        });
    }

    public getRowClass(formattedRecord: IFormattedUserNotification): string {
        let readState = UserNotificationState.Read as any;
        return formattedRecord.state === readState ? 'notification-read text-muted' : '';
    }

    getNotificationTextBySeverity(severity: abp.notifications.severity): string {
        switch (severity) {
            case abp.notifications.severity.SUCCESS:
                return this.l('Success');
            case abp.notifications.severity.WARN:
                return this.l('Warning');
            case abp.notifications.severity.ERROR:
                return this.l('Error');
            case abp.notifications.severity.FATAL:
                return this.l('Fatal');
            case abp.notifications.severity.INFO:
            default:
                return this.l('Info');
        }
    }

    sendNewRelease() {
        this.message.confirm('', this.l('AreYouSure'), (isConfirmed) => {
            if (isConfirmed) {
                this._mazarsNotificaionServiceProxy
                    .sendNewReleaseMessage()
                    .pipe(take(1))
                    .subscribe(() => {
                        this.notify.success(this.l('NotificationSendSuccessfullyMessage'));
                    });
            }
        });
    }

    showMessageDetailModal(dialog: DialogInfo): void {
        this._userNotificationHelper.openDetailModal(dialog);
    }
}
